import { environment } from '../../environments/environment';
import { API_ROUTING_CONFIG, ApiModule } from './api-routing.config';

export type BackendType = 'monolith' | 'microservice';

export type RouteDecision = {
  backend: BackendType;
  version: string;
  baseContext: string;
};

function trimTrailingSlash(value: string): string {
  let result = value;
  while (result.endsWith('/')) {
    result = result.slice(0, -1);
  }
  return result;
}

function trimLeadingSlash(value: string): string {
  let result = value;
  while (result.startsWith('/')) {
    result = result.slice(1);
  }
  return result;
}

export function getMonolithBaseContext(): string {
  const base = environment.api_base_url
    ? trimTrailingSlash(environment.api_base_url)
    : '';
  return `${base}/${API_ROUTING_CONFIG.monolithContext}/`;
}

export function getMicroserviceBaseContext(
  moduleName: ApiModule,
  baseUrl: string = environment.microservice_api_base_url
): string {
  const base = baseUrl ? trimTrailingSlash(baseUrl) : '';
  const moduleVersion = API_ROUTING_CONFIG.moduleVersions[moduleName] || 'v1';
  return `${base}/${API_ROUTING_CONFIG.microserviceContext}/${moduleName}/${moduleVersion}/`;
}

export function getRouteDecision(moduleName: ApiModule): RouteDecision {
  const useMicroservice =
    API_ROUTING_CONFIG.micro_architecture_flag && API_ROUTING_CONFIG.migratedModules[moduleName];

  return {
    backend: useMicroservice ? 'microservice' : 'monolith',
    version: API_ROUTING_CONFIG.moduleVersions[moduleName] || 'v1',
    baseContext: useMicroservice ? getMicroserviceBaseContext(moduleName) : getMonolithBaseContext()
  };
}

export function getModuleBaseContext(moduleName: ApiModule): string {
  return getRouteDecision(moduleName).baseContext;
}

export function getModuleBaseContextWithMonolithFallback(
  moduleName: ApiModule,
  monolithFallbackBaseContext: string
): string {
  const decision = getRouteDecision(moduleName);
  return decision.backend === 'microservice' ? decision.baseContext : monolithFallbackBaseContext;
}

export function resolveApiUrl(moduleName: ApiModule, resourcePath: string): string {
  return `${getModuleBaseContext(moduleName)}${trimLeadingSlash(resourcePath)}`;
}

export function detectBackendFromUrl(url: string): BackendType {
  const microBase = environment.microservice_api_base_url
    ? trimTrailingSlash(environment.microservice_api_base_url)
    : '';
  const microPrefix = `${microBase}/${API_ROUTING_CONFIG.microserviceContext}/`;
  return url.startsWith(microPrefix) ? 'microservice' : 'monolith';
}
